import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ClipboardCheck, ScanBarcode, Search, Loader2, CheckCircle2, XCircle,
  RotateCcw, ChevronDown, ChevronRight, Plus, Minus, X, AlertTriangle,
  Package, Play, Square, FileText,
} from 'lucide-react';
import { api } from '@/utils/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type InspectionType = 'rm' | 'in-process' | 'fg';

interface ChecklistItem {
  id: string;
  description: string;
  passed: boolean | null;
  notes: string;
}

interface MeasurementParam {
  id: string;
  name: string;
  value: string;
  min: string;
  max: string;
  unit: string;
}

interface InspectedProduct {
  id?: string;
  name: string;
  sku: string;
  barcode?: string;
  productionOrderNumber?: string;
  productionOrderId?: string;
  stageIndex?: number;
}

interface RecentInspection {
  barcode: string;
  product: string;
  result: 'pass' | 'fail' | 'rework';
  time: string;
  notes: string;
}

interface ProductOption {
  id: string;
  name: string;
  sku: string;
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_CHECKLISTS: Record<InspectionType, string[]> = {
  rm: [
    'Visual appearance OK',
    'Weight/quantity matches PO',
    'No damage or contamination',
    'Packaging intact',
    'Certificate/COA present',
  ],
  'in-process': [
    'Dimensions within tolerance',
    'Surface finish acceptable',
    'No visible defects',
    'Assembly correct',
    'Markings/labels correct',
  ],
  fg: [
    'Final visual inspection',
    'All QC stages passed',
    'Barcode applied correctly',
    'Packaging complete',
    'Ready for dispatch',
  ],
};

const TYPE_CONFIG: Record<InspectionType, { label: string; color: string; bgColor: string }> = {
  rm: { label: 'RM Inspection', color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200' },
  'in-process': { label: 'In-Process QC', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200' },
  fg: { label: 'FG Activation', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200' },
};

let _idCounter = 0;
function uid(): string {
  return `item-${++_idCounter}-${Date.now()}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FloorInspectionPage() {
  const scannerRef = useRef<HTMLInputElement>(null);

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(() => {
    return sessionStorage.getItem('floor_inspection_session') || null;
  });
  const [inspectionType, setInspectionType] = useState<InspectionType | null>(() => {
    return (sessionStorage.getItem('floor_inspection_type') as InspectionType) || null;
  });
  const [sessionStarting, setSessionStarting] = useState(false);

  // Counters
  const [passedCount, setPassedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  // Scan / Select state
  const [inputMode, setInputMode] = useState<'scan' | 'manual'>('scan');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  // Manual select
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productionOrderInput, setProductionOrderInput] = useState('');

  // Inspected product (after scan/select)
  const [inspectedProduct, setInspectedProduct] = useState<InspectedProduct | null>(null);

  // QC Checklist
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  // Parameters
  const [showParams, setShowParams] = useState(false);
  const [params, setParams] = useState<MeasurementParam[]>([]);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [resultFlash, setResultFlash] = useState<'pass' | 'fail' | null>(null);

  // Recent inspections
  const [recentInspections, setRecentInspections] = useState<RecentInspection[]>([]);

  // Summary modal
  const [showSummary, setShowSummary] = useState(false);

  // Persist session
  useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem('floor_inspection_session', sessionId);
    } else {
      sessionStorage.removeItem('floor_inspection_session');
    }
  }, [sessionId]);

  useEffect(() => {
    if (inspectionType) {
      sessionStorage.setItem('floor_inspection_type', inspectionType);
    } else {
      sessionStorage.removeItem('floor_inspection_type');
    }
  }, [inspectionType]);

  // Clear flash
  useEffect(() => {
    if (resultFlash) {
      const timer = setTimeout(() => setResultFlash(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [resultFlash]);

  // Auto-focus scanner when session active + scan mode
  useEffect(() => {
    if (sessionId && inputMode === 'scan' && !inspectedProduct) {
      setTimeout(() => scannerRef.current?.focus(), 100);
    }
  }, [sessionId, inputMode, inspectedProduct]);

  // Load products for manual mode
  useEffect(() => {
    if (inputMode === 'manual' && products.length === 0) {
      setProductsLoading(true);
      api.get('/products')
        .then((res) => {
          const items = res.data?.data || res.data || [];
          setProducts(
            Array.isArray(items)
              ? items.map((p: any) => ({ id: p.id, name: p.name, sku: p.sku || '—' }))
              : []
          );
        })
        .catch(() => setProducts([]))
        .finally(() => setProductsLoading(false));
    }
  }, [inputMode, products.length]);

  // ── Session Actions ──────────────────────────────────────────────────────

  const startSession = async () => {
    if (!inspectionType) return;
    setSessionStarting(true);
    try {
      const res = await api.post('/manufacturing-p0/floor/start-session', {
        type: inspectionType,
      });
      const data = res.data?.data || res.data;
      setSessionId(data.sessionId || data.id || `session-${Date.now()}`);
      setPassedCount(0);
      setFailedCount(0);
      setRecentInspections([]);
    } catch (err: any) {
      // Fallback: start local session if API unavailable
      setSessionId(`local-${Date.now()}`);
      setPassedCount(0);
      setFailedCount(0);
      setRecentInspections([]);
    } finally {
      setSessionStarting(false);
    }
  };

  const endSession = () => {
    setShowSummary(true);
  };

  const confirmEndSession = () => {
    setShowSummary(false);
    setSessionId(null);
    setInspectionType(null);
    setInspectedProduct(null);
    setChecklist([]);
    setParams([]);
    setPassedCount(0);
    setFailedCount(0);
    setRecentInspections([]);
    sessionStorage.removeItem('floor_inspection_session');
    sessionStorage.removeItem('floor_inspection_type');
  };

  // ── Lookup / Select ──────────────────────────────────────────────────────

  const handleBarcodeScan = async () => {
    const code = barcodeInput.trim();
    if (!code || lookingUp) return;

    setLookingUp(true);
    try {
      const res = await api.get(`/product-units/${encodeURIComponent(code)}/lookup`);
      const data = res.data?.data || res.data;
      setInspectedProduct({
        id: data.productId || data.product?.id || data.id,
        name: data.product?.name || data.productName || 'Unknown Product',
        sku: data.product?.sku || data.sku || '—',
        barcode: code,
        productionOrderNumber: data.productionOrder?.number || data.productionOrderNumber || '—',
        productionOrderId: data.productionOrderId || data.productionOrder?.id || undefined,
      });
      buildChecklist();
    } catch {
      // If lookup fails, still allow inspection with just the barcode
      setInspectedProduct({
        name: 'Unknown (barcode lookup failed)',
        sku: '—',
        barcode: code,
        productionOrderNumber: '—',
      });
      buildChecklist();
    } finally {
      setLookingUp(false);
      setBarcodeInput('');
    }
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan();
    }
  };

  const handleManualSelect = () => {
    const prod = products.find((p) => p.id === selectedProductId);
    if (!prod) return;
    setInspectedProduct({
      id: prod.id,
      name: prod.name,
      sku: prod.sku,
      productionOrderNumber: productionOrderInput || '—',
    });
    buildChecklist();
  };

  const buildChecklist = useCallback(() => {
    if (!inspectionType) return;
    const items = DEFAULT_CHECKLISTS[inspectionType];
    setChecklist(items.map((desc) => ({ id: uid(), description: desc, passed: null, notes: '' })));
    setParams([]);
    setShowParams(false);
  }, [inspectionType]);

  // ── Checklist Handlers ───────────────────────────────────────────────────

  const toggleChecklistItem = (id: string, val: boolean) => {
    setChecklist((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, passed: item.passed === val ? null : val } : item
      )
    );
  };

  const updateChecklistNote = (id: string, notes: string) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, notes } : item))
    );
  };

  // ── Params Handlers ──────────────────────────────────────────────────────

  const addParam = () => {
    setParams((prev) => [...prev, { id: uid(), name: '', value: '', min: '', max: '', unit: '' }]);
  };

  const updateParam = (id: string, field: keyof MeasurementParam, val: string) => {
    setParams((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: val } : p))
    );
  };

  const removeParam = (id: string) => {
    setParams((prev) => prev.filter((p) => p.id !== id));
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const submitInspection = async (result: 'pass' | 'fail' | 'rework') => {
    if (!inspectedProduct || !sessionId || submitting) return;

    setSubmitting(true);
    try {
      // Create inspection record
      const inspectRes = await api.post('/manufacturing-p0/floor/inspect', {
        sessionId,
        type: inspectionType,
        scannedBarcode: inspectedProduct.barcode || null,
        productId: inspectedProduct.id || null,
        productionOrderId: inspectedProduct.productionOrderId || null,
        stageIndex: inspectedProduct.stageIndex ?? null,
        checklist: checklist.map((c) => ({
          item: c.description,
          passed: c.passed,
          notes: c.notes,
        })),
        parameters: params
          .filter((p) => p.name)
          .map((p) => ({
            name: p.name,
            value: p.value,
            min: p.min,
            max: p.max,
            unit: p.unit,
          })),
      });

      const inspectionId = inspectRes.data?.data?.id || inspectRes.data?.id;

      // Mark pass or fail (rework is sent as fail with action=rework)
      if (inspectionId) {
        if (result === 'pass') {
          await api.post(`/manufacturing-p0/floor/inspect/${inspectionId}/pass`);
        } else {
          await api.post(`/manufacturing-p0/floor/inspect/${inspectionId}/fail`, {
            reason: result === 'rework' ? 'Rework required' : 'Failed QC inspection',
            action: result === 'rework' ? 'rework' : 'reject',
          });
        }
      }

      // Update counters
      if (result === 'pass') {
        setPassedCount((c) => c + 1);
        setResultFlash('pass');
      } else {
        setFailedCount((c) => c + 1);
        setResultFlash('fail');
      }

      // Add to recents
      const failNotes = checklist
        .filter((c) => c.passed === false)
        .map((c) => c.description)
        .join(', ');

      setRecentInspections((prev) => [
        {
          barcode: inspectedProduct.barcode || inspectedProduct.name,
          product: inspectedProduct.name,
          result,
          time: new Date().toISOString(),
          notes: result !== 'pass' ? failNotes : '',
        },
        ...prev.slice(0, 19),
      ]);
    } catch (err: any) {
      // Even on API error, track locally
      if (result === 'pass') {
        setPassedCount((c) => c + 1);
        setResultFlash('pass');
      } else {
        setFailedCount((c) => c + 1);
        setResultFlash('fail');
      }
      setRecentInspections((prev) => [
        {
          barcode: inspectedProduct.barcode || inspectedProduct.name,
          product: inspectedProduct.name,
          result,
          time: new Date().toISOString(),
          notes: '',
        },
        ...prev.slice(0, 19),
      ]);
    } finally {
      setSubmitting(false);
      setInspectedProduct(null);
      setChecklist([]);
      setParams([]);
      setShowParams(false);
      setSelectedProductId('');
      setProductionOrderInput('');
      setTimeout(() => scannerRef.current?.focus(), 100);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const totalInspected = passedCount + failedCount;
  const passRate = totalInspected > 0 ? Math.round((passedCount / totalInspected) * 100) : 0;

  const filteredProducts = products.filter((p) =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
            <ClipboardCheck size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Floor Inspection</h1>
            <p className="text-sm text-gray-500">On-floor RM / in-process / FG inspection via barcode or product selection</p>
          </div>
        </div>
        {sessionId && (
          <div className="flex items-center gap-3">
            <div className={`rounded-full border px-3 py-1 text-xs font-semibold ${TYPE_CONFIG[inspectionType!]?.bgColor || 'bg-gray-50 border-gray-200'} ${TYPE_CONFIG[inspectionType!]?.color || 'text-gray-700'}`}>
              {TYPE_CONFIG[inspectionType!]?.label} — {totalInspected} inspected
            </div>
            <button
              onClick={endSession}
              className="flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <Square size={14} />
              End Session
            </button>
          </div>
        )}
      </div>

      {/* ── Session Setup ─────────────────────────────────────────────── */}
      {!sessionId && (
        <div className="mb-6">
          <h2 className="mb-3 text-base font-semibold text-slate-900">Select Inspection Type</h2>
          <div className="mb-4 grid grid-cols-3 gap-4">
            {(['rm', 'in-process', 'fg'] as InspectionType[]).map((type) => {
              const cfg = TYPE_CONFIG[type];
              const selected = inspectionType === type;
              return (
                <button
                  key={type}
                  onClick={() => setInspectionType(type)}
                  className={`rounded-lg border-2 p-4 text-left transition-all ${
                    selected
                      ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] ring-2 ring-[var(--ink-3)]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className={`text-sm font-semibold ${selected ? 'text-[var(--ink-0)]' : 'text-slate-900'}`}>
                    {cfg.label}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {type === 'rm' && 'Incoming raw material quality check'}
                    {type === 'in-process' && 'Mid-production quality verification'}
                    {type === 'fg' && 'Final goods activation and verification'}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={startSession}
            disabled={!inspectionType || sessionStarting}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sessionStarting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            Start Session
          </button>
        </div>
      )}

      {/* ── Scan/Select Input (session active, no product selected) ──── */}
      {sessionId && !inspectedProduct && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
          {/* Tabs */}
          <div className="mb-4 flex border-b border-gray-200">
            <button
              onClick={() => setInputMode('scan')}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                inputMode === 'scan'
                  ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2"><ScanBarcode size={16} /> Barcode Scan</span>
            </button>
            <button
              onClick={() => setInputMode('manual')}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                inputMode === 'manual'
                  ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2"><Search size={16} /> Manual Select</span>
            </button>
          </div>

          {inputMode === 'scan' ? (
            <div className="flex gap-3">
              <div className="relative flex-1">
                <ScanBarcode size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={scannerRef}
                  type="text"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={handleBarcodeKeyDown}
                  placeholder="Scan barcode here..."
                  className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-lg font-mono text-slate-900 placeholder-gray-400 focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]/20"
                  autoComplete="off"
                  disabled={lookingUp}
                />
              </div>
              <button
                onClick={handleBarcodeScan}
                disabled={!barcodeInput.trim() || lookingUp}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-5 py-3 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {lookingUp ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Lookup
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Product</label>
                {productsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Loading products...
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search products..."
                      className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-gray-400 focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]/20"
                    />
                    <select
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]/20"
                    >
                      <option value="">Select a product</option>
                      {filteredProducts.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.sku})
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Production Order (optional)</label>
                <input
                  type="text"
                  value={productionOrderInput}
                  onChange={(e) => setProductionOrderInput(e.target.value)}
                  placeholder="e.g. PRD-2026-0042"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-gray-400 focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]/20"
                />
              </div>
              <button
                onClick={handleManualSelect}
                disabled={!selectedProductId}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <CheckCircle2 size={16} />
                Select & Inspect
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Inspection Form ───────────────────────────────────────────── */}
      {sessionId && inspectedProduct && (
        <div className={`mb-6 rounded-lg border bg-white transition-all duration-300 ${
          resultFlash === 'pass'
            ? 'border-green-300 ring-2 ring-green-200'
            : resultFlash === 'fail'
            ? 'border-red-300 ring-2 ring-red-200'
            : 'border-gray-200'
        }`}>
          {/* Product Info */}
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package size={20} className="text-gray-400" />
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{inspectedProduct.name}</h3>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>SKU: {inspectedProduct.sku}</span>
                    {inspectedProduct.barcode && <span className="font-mono">Barcode: {inspectedProduct.barcode}</span>}
                    <span>PRD Order: {inspectedProduct.productionOrderNumber}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setInspectedProduct(null);
                  setChecklist([]);
                  setParams([]);
                }}
                className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* QC Checklist */}
          <div className="px-5 py-4">
            <h4 className="mb-3 text-sm font-semibold text-slate-900">QC Checklist</h4>
            <div className="space-y-2">
              {checklist.map((item) => (
                <div key={item.id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="flex gap-1.5 pt-0.5">
                    <button
                      onClick={() => toggleChecklistItem(item.id, true)}
                      className={`rounded p-1 transition-colors ${
                        item.passed === true
                          ? 'bg-green-100 text-green-600'
                          : 'text-gray-300 hover:bg-green-50 hover:text-green-400'
                      }`}
                      title="Pass"
                    >
                      <CheckCircle2 size={18} />
                    </button>
                    <button
                      onClick={() => toggleChecklistItem(item.id, false)}
                      className={`rounded p-1 transition-colors ${
                        item.passed === false
                          ? 'bg-red-100 text-red-600'
                          : 'text-gray-300 hover:bg-red-50 hover:text-red-400'
                      }`}
                      title="Fail"
                    >
                      <XCircle size={18} />
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">{item.description}</p>
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => updateChecklistNote(item.id, e.target.value)}
                      placeholder="Notes (optional)"
                      className="mt-1 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs text-slate-600 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Parameters Section */}
          <div className="border-t border-gray-100 px-5 py-4">
            <button
              onClick={() => setShowParams(!showParams)}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
            >
              {showParams ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Measurement Parameters
              {params.length > 0 && (
                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{params.length}</span>
              )}
            </button>
            {showParams && (
              <div className="mt-3 space-y-2">
                {params.map((p) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={p.name}
                      onChange={(e) => updateParam(p.id, 'name', e.target.value)}
                      placeholder="Parameter"
                      className="w-32 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                    <input
                      type="text"
                      value={p.value}
                      onChange={(e) => updateParam(p.id, 'value', e.target.value)}
                      placeholder="Value"
                      className="w-20 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                    <input
                      type="text"
                      value={p.min}
                      onChange={(e) => updateParam(p.id, 'min', e.target.value)}
                      placeholder="Min"
                      className="w-16 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                    <input
                      type="text"
                      value={p.max}
                      onChange={(e) => updateParam(p.id, 'max', e.target.value)}
                      placeholder="Max"
                      className="w-16 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                    <input
                      type="text"
                      value={p.unit}
                      onChange={(e) => updateParam(p.id, 'unit', e.target.value)}
                      placeholder="Unit"
                      className="w-16 rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder-gray-400 focus:border-[var(--ink-3)] focus:outline-none"
                    />
                    <button onClick={() => removeParam(p.id)} className="rounded p-1 text-gray-400 hover:text-red-500">
                      <Minus size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addParam}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--ink-0)] hover:text-[var(--ink-0)]"
                >
                  <Plus size={14} /> Add Parameter
                </button>
              </div>
            )}
          </div>

          {/* Pass / Fail / Rework Buttons */}
          <div className="flex items-center gap-3 border-t border-gray-100 px-5 py-4">
            <button
              onClick={() => submitInspection('pass')}
              disabled={submitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Pass
            </button>
            <button
              onClick={() => submitInspection('fail')}
              disabled={submitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
              Fail
            </button>
            <button
              onClick={() => submitInspection('rework')}
              disabled={submitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
              Rework
            </button>
          </div>
        </div>
      )}

      {/* ── Session Progress ──────────────────────────────────────────── */}
      {sessionId && (
        <>
          {/* Counter Cards */}
          <div className="mb-4 grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items Inspected</p>
              <p className="text-2xl font-bold text-slate-900">{totalInspected}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Passed</p>
              <p className="text-2xl font-bold text-green-600">{passedCount}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Failed</p>
              <p className="text-2xl font-bold text-red-600">{failedCount}</p>
            </div>
          </div>

          {/* Progress Bar */}
          {totalInspected > 0 && (
            <div className="mb-6">
              <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                <span>Pass Rate</span>
                <span className="font-semibold text-slate-700">{passRate}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-green-500 transition-all duration-500"
                  style={{ width: `${passRate}%` }}
                />
              </div>
            </div>
          )}

          {/* Recent Inspections */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">Recent Inspections</h2>
            </div>
            {recentInspections.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <ClipboardCheck size={36} className="mb-2 opacity-40" />
                <p className="text-sm">No inspections yet. Scan a barcode or select a product.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-left">
                      <th className="px-5 py-3 font-medium text-gray-500">Barcode / Product</th>
                      <th className="px-5 py-3 font-medium text-gray-500">Result</th>
                      <th className="px-5 py-3 font-medium text-gray-500">Time</th>
                      <th className="px-5 py-3 font-medium text-gray-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInspections.slice(0, 10).map((item, i) => (
                      <tr key={`${item.barcode}-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-5 py-3 font-mono text-xs text-slate-900">{item.barcode}</td>
                        <td className="px-5 py-3">
                          {item.result === 'pass' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              <CheckCircle2 size={12} /> Pass
                            </span>
                          ) : item.result === 'rework' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                              <RotateCcw size={12} /> Rework
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                              <XCircle size={12} /> Fail
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-500">{formatTime(item.time)}</td>
                        <td className="px-5 py-3 text-xs text-gray-500">{item.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── End Session Summary Modal ─────────────────────────────────── */}
      {showSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <FileText size={24} className="text-[var(--ink-0)]" />
              <h3 className="text-lg font-semibold text-slate-900">Session Summary</h3>
            </div>
            <div className="mb-4 space-y-3 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Inspection Type</span>
                <span className="font-medium text-slate-900">{inspectionType ? TYPE_CONFIG[inspectionType].label : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Inspected</span>
                <span className="font-bold text-slate-900">{totalInspected}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Passed</span>
                <span className="font-bold text-green-600">{passedCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Failed</span>
                <span className="font-bold text-red-600">{failedCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Pass Rate</span>
                <span className="font-bold text-slate-900">{passRate}%</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSummary(false)}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Continue
              </button>
              <button
                onClick={confirmEndSession}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
